import { Grid } from "@mui/material";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClearFilterButton } from "../../../components/Buttons/clear-filter-button";
import { LoginAsDialog } from "../../auth/login-as-dialog.component";
import SearchField from "../../../components/SearchField";
import { CustomTable } from "../../../components/Tables/custom-table";
import { useUserTableContext } from "../../../providers/Users/user-table.provider";
import { useBaseDataGridStyles } from "../../../style/baseDataGrid.style";
import { Shades } from "../../../style/Colors";
import { IUserLoginAs } from "../../../domain/user/user";
import { IUserTableRow } from "../../../domain/user/user-table-row";
import { getRolePomTranslation } from "../../../domain/user/role-pom";
import { getGroupTypeTranslation } from "../../../utils/group.helper";
import { UserDetailDialog } from "../../../pages/groups/components/dialogues/user-detail-dialog.component";
import { UsersActiveFilter } from "./filter/users-active-filter";
import { HasLoginIndicator } from "../has-login-indicator.component";

const UsersTableToolbar: FC = () => {
  const { t } = useTranslation();
  const { classes } = useBaseDataGridStyles();
  const { filters, setFilters, tablePagination, setTablePagination, isFilterDirty, resetFilter } = useUserTableContext();

  return (
    <Grid direction="row" container className={classes.search}>
      <Grid className={classes.searchGrid} container spacing={4}>
        <Grid item>
          <UsersActiveFilter />
        </Grid>
        {isFilterDirty && (
          <Grid item>
            <ClearFilterButton onClick={resetFilter} />
          </Grid>
        )}
        <Grid className={classes.searchGrid} item>
          <SearchField
            tooltip={t("basedata.users.table.search.lastName")}
            submitSearch={true}
            inputTimeout={0}
            initialValue={filters?.lastNameQuery}
            onSearchSubmit={(value: string) => {
              setTablePagination({ ...tablePagination, page: 0 });
              setFilters({ ...filters, lastNameQuery: value });
            }}
            placeholder={t("basedata.users.table.search.lastName")}
          />
        </Grid>
        <Grid className={classes.searchGrid} item>
          <SearchField
            tooltip={t("basedata.users.table.search.email")}
            submitSearch={true}
            inputTimeout={0}
            initialValue={filters?.emailQuery}
            onSearchSubmit={(value: string) => {
              setTablePagination({ ...tablePagination, page: 0 });
              setFilters({ ...filters, emailQuery: value });
            }}
            placeholder={t("basedata.users.table.search.email")}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export const UsersTable: FC = () => {
  const { t } = useTranslation();
  const [loginAsModal, setLoginAsModal] = useState<IUserTableRow>();
  const [selectedUser, setSelectedUser] = useState<IUserTableRow>();
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { users, isLoading, setTablePagination, tablePagination } = useUserTableContext();

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "groupType",
        headerName: t("basedata.users.table.type"),
        flex: 1,
        renderCell: (params) => {
          const groupType = (params.row as IUserTableRow).groupType;
          return groupType !== undefined ? getGroupTypeTranslation(groupType, t) : null;
        },
      },
      {
        field: "rolePom",
        headerName: t("basedata.users.table.role"),
        flex: 1,
        renderCell: (params) => {
          const rolePom = (params.row as IUserTableRow).rolePom;
          return rolePom !== undefined ? t(getRolePomTranslation(rolePom)) : null;
        },
      },
      {
        field: "erpNumber",
        headerName: t("basedata.users.table.erp_number"),
        flex: 1,
      },
      {
        field: "groupName",
        headerName: t("basedata.users.table.groupName"),
        flex: 1,
      },
      {
        field: "fullName",
        headerName: t("basedata.users.table.name"),
        flex: 1,
      },
      {
        field: "email",
        headerName: t("basedata.users.table.email"),
        flex: 1,
      },
      {
        field: "hasLogin",
        headerName: t("basedata.users.table.login"),
        description: t("basedata.users.table.hasLogin"),
        // Same flex as the other columns, so the centred icon leaves as much edge space as the first column.
        flex: 1,
        sortable: false,
        display: "flex",
        renderCell: (params) => <HasLoginIndicator hasLogin={(params.row as IUserTableRow).hasLogin} />,
      },
    ],
    [t],
  );

  return (
    <>
      <LoginAsDialog
        user={
          loginAsModal
            ? ({
                firstName: loginAsModal.firstName,
                lastName: loginAsModal.lastName,
                username: loginAsModal.username,
              } as IUserLoginAs)
            : undefined
        }
        open={!!loginAsModal}
        onCancel={() => setLoginAsModal(undefined)}
      />
      <UserDetailDialog
        open={isDetailOpen}
        user={selectedUser}
        onClose={() => {
          setIsDetailOpen(false);
        }}
        onLoginAsClick={() => {
          if (selectedUser) {
            setLoginAsModal(selectedUser);
          }
          setIsDetailOpen(false);
        }}
        showJobTitleAndExternalId={true}
      />
      <CustomTable
        columns={columns}
        rows={users}
        loading={isLoading}
        getRowId={(row) => row.rowKey}
        onRowClick={(row) => {
          setSelectedUser(row);
          setIsDetailOpen(true);
        }}
        getRowClassName={(row) => (!row.active ? "row-inactive" : "")}
        paginationModel={{ page: tablePagination.page, pageSize: tablePagination.pageSize }}
        onPaginationModelChange={(model: GridPaginationModel) =>
          setTablePagination({ ...tablePagination, page: model.page, pageSize: model.pageSize })
        }
        rowCount={tablePagination.numberOfRecords}
        pageSizeOptions={[50, 100]}
        paginationMode="server"
        slots={{ toolbar: UsersTableToolbar }}
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell": {
            backgroundColor: Shades.gray20,
          },
          "& .row-inactive": { color: "red" },
        }}
      />
    </>
  );
};
